import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCircle,
  FileText,
  Clock,
  Heart,
  Home,
  LogOut,
  MapPin,
  RefreshCw,
  User,
  Users,
  Wallet
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import InstallAppButton from '../components/InstallAppButton';
import { EventsTab, FraternityTab, NotificationsTab, ResourcesTab } from '../components/member/MemberSections';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const formatAmount = (value) =>
  `${new Intl.NumberFormat('fr-FR').format(Number(value) || 0)} F CFA`;

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR');
};

const TABS = [
  { id: 'overview', label: 'Mon profil', icon: User },
  { id: 'contributions', label: 'Cotisations', icon: Wallet },
  { id: 'fraternity', label: 'Fraternité', icon: Users },
  { id: 'events', label: 'Événements', icon: CalendarDays },
  { id: 'resources', label: 'Ressources', icon: FileText },
  { id: 'notifications', label: 'Notifications', icon: Bell }
];

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-words min-w-0">{value || '—'}</span>
  </div>
);

const MemberDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiService.get('/member/portal', { params: { year } });
      setData(response.data);
    } catch (err) {
      setError(err.message || 'Impossible de charger vos informations');
    } finally {
      setIsLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Rafraîchit régulièrement pour faire apparaître les nouvelles notifications dans la cloche
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    }, 60000);
    return () => clearInterval(timer);
  }, [loadData]);

  const markNotifications = (shouldMark) => {
    setData((prev) => prev && ({
      ...prev,
      notifications: prev.notifications.map((n) => (shouldMark(n) ? { ...n, read: true } : n))
    }));
  };

  const handleMarkRead = async (id) => {
    try {
      await apiService.post(`/member/notifications/${id}/read`, {});
      markNotifications((n) => n.id === id);
    } catch (err) {
      setError(err.message || 'Impossible de marquer la notification comme lue');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiService.post('/member/notifications/read-all', {});
      markNotifications(() => true);
    } catch (err) {
      setError(err.message || 'Impossible de marquer les notifications comme lues');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const profile = data?.profile || user || {};
  const contributions = data?.contributions;
  const notifications = data?.notifications || [];
  const unreadCount = notifications.filter((n) => !n.read).length;
  const paidPercent = contributions?.total_due > 0
    ? Math.min(100, Math.round((contributions.total_paid / contributions.total_due) * 100))
    : 0;
  const years = contributions?.available_years?.length
    ? Array.from(new Set([...contributions.available_years, year])).sort((a, b) => b - a)
    : [year];

  return (
    <div className="min-h-screen bg-gray-50 pb-8 text-left overflow-x-hidden">
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1 text-sm text-indigo-100 hover:text-white">
              <Home className="w-4 h-4" /> Accueil
            </Link>
            <div className="flex items-center gap-3">
              <button onClick={loadData} aria-label="Actualiser" className="text-indigo-100 hover:text-white">
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={handleLogout} aria-label="Déconnexion" className="text-indigo-100 hover:text-white">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          <h1 className="mt-4 text-xl sm:text-2xl font-bold break-words">
            Bonjour {profile.first_name} {profile.last_name}
          </h1>
          <p className="text-indigo-100 text-sm mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {profile.zone && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Zone {profile.zone}</span>}
            {profile.fraternity && <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {profile.fraternity}</span>}
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 -mt-4 space-y-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-sm font-medium text-gray-800 mb-2">📱 Application mobile FSF</p>
          <InstallAppButton className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-3 px-4 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all" />
        </div>

        <nav className="bg-white rounded-xl shadow grid grid-cols-3 sm:grid-cols-6">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex flex-col items-center gap-1 px-1 py-3 text-[11px] sm:text-sm font-medium border-b-2 ${
                activeTab === id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500'
              }`}
            >
              <span className="relative inline-flex">
                <Icon className="w-5 h-5" />
                {id === 'notifications' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-red-500 text-white rounded-full text-[10px] leading-none">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </span>
              <span className="truncate max-w-full">{label}</span>
            </button>
          ))}
        </nav>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
        )}

        {isLoading && !data ? (
          <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">Chargement…</div>
        ) : data && (
          <>
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <section className="bg-white rounded-xl shadow p-4">
                  <h2 className="font-semibold text-gray-900 mb-2">Mes informations</h2>
                  <InfoRow label="Nom" value={`${profile.first_name || ''} ${profile.last_name || ''}`} />
                  <InfoRow label="Nom du couple" value={profile.couple_name} />
                  {data.spouse && <InfoRow label="Conjoint(e)" value={`${data.spouse.first_name} ${data.spouse.last_name}`} />}
                  <InfoRow label="Zone" value={profile.zone} />
                  <InfoRow label="Fraternité" value={profile.fraternity} />
                  <InfoRow label="Email" value={profile.email} />
                  <InfoRow label="Téléphone" value={profile.phone} />
                  <InfoRow label="Date d'adhésion" value={formatDate(profile.join_date)} />
                  <InfoRow label="Cotisation mensuelle" value={formatAmount(profile.contribution_monthly)} />
                  <div className="pt-3">
                    <Link to="/profile" className="text-sm text-indigo-600 font-medium">Modifier mon profil →</Link>
                  </div>
                </section>

                <section className="bg-white rounded-xl shadow p-4">
                  <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-500" /> Mes dons
                  </h2>
                  {data.dons.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucun don enregistré.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {data.dons.map((don) => (
                        <li key={don.id} className="py-2 flex justify-between text-sm">
                          <span className="text-gray-700">
                            {don.objet_don || don.projet_soutenu || 'Don'}
                            <span className="block text-xs text-gray-400">{formatDate(don.created_at)}</span>
                          </span>
                          <span className="font-medium text-gray-900">{formatAmount(don.montant)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            )}

            {activeTab === 'contributions' && contributions && (
              <section className="bg-white rounded-xl shadow p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h2 className="font-semibold text-gray-900">Cotisations {contributions.year}</h2>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg text-sm px-2 py-1"
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center mb-3">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-500">Dû</p>
                    <p className="text-sm font-semibold">{formatAmount(contributions.total_due)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-xs text-green-600">Payé</p>
                    <p className="text-sm font-semibold text-green-700">{formatAmount(contributions.total_paid)}</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2">
                    <p className="text-xs text-orange-600">Reste</p>
                    <p className="text-sm font-semibold text-orange-700">{formatAmount(contributions.balance)}</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: `${paidPercent}%` }} />
                </div>
                {contributions.items.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune cotisation enregistrée pour {contributions.year}.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {contributions.items.map((item) => (
                      <li key={item.id} className="py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
                        <span className="text-gray-700">{MONTHS[item.period_month - 1]} {item.period_year}</span>
                        <span className="flex items-center gap-2">
                          <span className="font-medium">{formatAmount(item.amount)}</span>
                          {item.status === 'paid' ? (
                            <span className="flex items-center gap-1 text-green-600 text-xs"><CheckCircle className="w-4 h-4" /> Payée</span>
                          ) : (
                            <span className="flex items-center gap-1 text-orange-600 text-xs"><Clock className="w-4 h-4" /> En attente</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {activeTab === 'fraternity' && (
              <FraternityTab
                fraternity={profile.fraternity}
                zone={profile.zone}
                members={data.fraternity_members || []}
              />
            )}

            {activeTab === 'events' && <EventsTab events={data.events || []} onChanged={loadData} />}

            {activeTab === 'resources' && <ResourcesTab />}

            {activeTab === 'notifications' && (
              <NotificationsTab
                notifications={notifications}
                onMarkRead={handleMarkRead}
                onMarkAllRead={handleMarkAllRead}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default MemberDashboard;
